import React, { useState } from 'react';
import Cookies from 'js-cookie';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.jsx';
import { Button } from '../ui/button.jsx';
import { Input } from '../ui/input.jsx';
import { Label } from '../ui/label.jsx';
import { Textarea } from '../ui/textarea.jsx';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Globe, 
  Target, 
  TrendingUp,
  DollarSign,
  Award,
  CheckCircle,
  Users
} from 'lucide-react';

const AffiliateRegistration = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    website: '',
    social_media: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    },
    target_audience: '',
    marketing_experience: '',
    referral_cookie_duration: 30
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = Cookies.get('enakid_token');
      
      // The backend endpoint expects user_id to be passed, but we get it from the auth token
      // So we need to modify the backend to get user_id from the current_user instead
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/affiliates/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success('Affiliate application submitted successfully!');
        // Redirect to affiliate dashboard or show success message
        setTimeout(() => {
          window.location.href = '/affiliate';
        }, 2000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.message || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Join the TavPlans Affiliate Program
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Partner with us to earn recurring commissions by referring businesses to TavPlans' 
          powerful financial modeling platform.
        </p>
      </div>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="text-center">
          <CardContent className="pt-6">
            <DollarSign className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">High Commissions</h3>
            <p className="text-sm text-gray-600">
              Earn 20-35% recurring revenue share based on your performance tier
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <Award className="h-12 w-12 text-purple-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Tier System</h3>
            <p className="text-sm text-gray-600">
              Bronze to Platinum tiers with increasing commission rates and bonuses
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <TrendingUp className="h-12 w-12 text-blue-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Real-time Tracking</h3>
            <p className="text-sm text-gray-600">
              Comprehensive analytics dashboard with detailed performance metrics
            </p>
          </CardContent>
        </Card>

        <Card className="text-center">
          <CardContent className="pt-6">
            <Users className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">Marketing Support</h3>
            <p className="text-sm text-gray-600">
              Access to marketing materials, landing pages, and promotional content
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Commission Tiers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Commission Tiers</CardTitle>
          <CardDescription>
            Your commission rate increases as you refer more customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-200">
              <Award className="h-8 w-8 text-amber-600 mx-auto mb-2" />
              <h4 className="font-semibold text-amber-800">Bronze</h4>
              <p className="text-2xl font-bold text-amber-600">20%</p>
              <p className="text-xs text-amber-700">0-10 referrals</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Award className="h-8 w-8 text-gray-600 mx-auto mb-2" />
              <h4 className="font-semibold text-gray-800">Silver</h4>
              <p className="text-2xl font-bold text-gray-600">25%</p>
              <p className="text-xs text-gray-700">11-25 referrals</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <Award className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
              <h4 className="font-semibold text-yellow-800">Gold</h4>
              <p className="text-2xl font-bold text-yellow-600">30%</p>
              <p className="text-xs text-yellow-700">26-50 referrals</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <Award className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <h4 className="font-semibold text-purple-800">Platinum</h4>
              <p className="text-2xl font-bold text-purple-600">35%</p>
              <p className="text-xs text-purple-700">51+ referrals</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Application Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center">
            <UserPlus className="h-5 w-5 mr-2" />
            Affiliate Application
          </CardTitle>
          <CardDescription>
            Tell us about yourself and your marketing experience
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="business_name">Business/Company Name</Label>
                  <Input
                    id="business_name"
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    placeholder="Your business name (optional)"
                  />
                </div>
                
                <div>
                  <Label htmlFor="website">Website URL</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    placeholder="https://yourwebsite.com (optional)"
                  />
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Globe className="h-5 w-5 mr-2" />
                Social Media Presence
              </h3>
              <p className="text-sm text-gray-600">
                Share your social media profiles to help us understand your reach
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="linkedin">LinkedIn Profile</Label>
                  <Input
                    id="linkedin"
                    type="url"
                    value={formData.social_media.linkedin}
                    onChange={(e) => handleInputChange('social_media.linkedin', e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                  />
                </div>
                
                <div>
                  <Label htmlFor="twitter">Twitter/X Profile</Label>
                  <Input
                    id="twitter"
                    type="url"
                    value={formData.social_media.twitter}
                    onChange={(e) => handleInputChange('social_media.twitter', e.target.value)}
                    placeholder="https://twitter.com/yourhandle"
                  />
                </div>
                
                <div>
                  <Label htmlFor="facebook">Facebook Page</Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={formData.social_media.facebook}
                    onChange={(e) => handleInputChange('social_media.facebook', e.target.value)}
                    placeholder="https://facebook.com/yourpage"
                  />
                </div>
                
                <div>
                  <Label htmlFor="instagram">Instagram Profile</Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={formData.social_media.instagram}
                    onChange={(e) => handleInputChange('social_media.instagram', e.target.value)}
                    placeholder="https://instagram.com/yourprofile"
                  />
                </div>
              </div>
            </div>

            {/* Target Audience */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Target className="h-5 w-5 mr-2" />
                Marketing Information
              </h3>
              
              <div>
                <Label htmlFor="target_audience">Target Audience</Label>
                <Textarea
                  id="target_audience"
                  value={formData.target_audience}
                  onChange={(e) => handleInputChange('target_audience', e.target.value)}
                  placeholder="Describe your target audience (e.g., small business owners, startups, consultants, etc.)"
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="marketing_experience">Marketing Experience</Label>
                <Textarea
                  id="marketing_experience"
                  value={formData.marketing_experience}
                  onChange={(e) => handleInputChange('marketing_experience', e.target.value)}
                  placeholder="Tell us about your marketing experience and how you plan to promote TavPlans"
                  rows={4}
                />
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-blue-800 font-medium mb-1">What happens next?</p>
                    <ul className="text-blue-700 space-y-1">
                      <li>• Your application will be reviewed within 1-2 business days</li>
                      <li>• Once approved, you'll receive your unique affiliate code</li>
                      <li>• Access to marketing materials and tracking dashboard</li>
                      <li>• Start earning commissions from your first referral</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                size="lg"
              >
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Additional Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Who Should Apply?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Business Consultants & Advisors</p>
                <p className="text-sm text-gray-600">Help clients with financial planning</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Accounting Firms</p>
                <p className="text-sm text-gray-600">Offer advanced modeling to clients</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Business Development Professionals</p>
                <p className="text-sm text-gray-600">Network with startups and SMBs</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Content Creators & Influencers</p>
                <p className="text-sm text-gray-600">Business and finance focused audience</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Support & Resources</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Marketing Materials</p>
                <p className="text-sm text-gray-600">Banners, email templates, case studies</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Custom Landing Pages</p>
                <p className="text-sm text-gray-600">Personalized pages for your audience</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Dedicated Support</p>
                <p className="text-sm text-gray-600">Direct access to our affiliate team</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Performance Tracking</p>
                <p className="text-sm text-gray-600">Real-time analytics and reporting</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AffiliateRegistration;